import type { ArtefactId, AssetResolverManager } from "@/core/asset-resolver"
import type { Photo } from "@/core/photo"
import type { Mask } from "@/core/types"
import { isObjectMask } from "@/core/utils/masks"
import type { AIMaskResolver, MaskTextureLease } from "@/io/assets/ai-mask/ai-mask-resolver"
import { isAbortError } from "@/utils/error"

const transientAssetScopeId = "mask-overlay-preview"
const updateDelay = 50

type MaskOverlayAssets = Pick<
	AssetResolverManager,
	"resolve" | "setTransientAssets" | "clearTransientAssets"
>

interface MaskOverlayEditor {
	photo: Photo
	renderer: { assets: MaskOverlayAssets }
	setOverlays(maskIds: Set<string> | null, previewMasks?: Mask[] | null): void
}

export class MaskOverlayController {
	private requestId = 0
	private updateTimeout?: number
	private pendingController?: AbortController
	private activeTransientLeases: MaskTextureLease[] = []
	private activeTransientMaskIds = new Set<string>()

	constructor(
		private readonly getEditor: () => MaskOverlayEditor,
		private readonly maskResolver: Pick<AIMaskResolver, "acquireMaskTexture">,
	) {}

	schedule(maskOverlayId: string | null, previewMasks: Mask[]) {
		this.requestId += 1
		this.pendingController?.abort()
		this.pendingController = undefined
		if (this.updateTimeout !== undefined) {
			globalThis.clearTimeout(this.updateTimeout)
		}
		this.updateTimeout = globalThis.setTimeout(() => {
			this.updateTimeout = undefined
			void this.update(maskOverlayId, previewMasks).catch((error: unknown) => {
				console.error("Failed to update transient mask overlay", error)
			})
		}, updateDelay) as unknown as number
	}

	async update(maskOverlayId: string | null, previewMasks: Mask[]) {
		if (this.updateTimeout !== undefined) {
			globalThis.clearTimeout(this.updateTimeout)
			this.updateTimeout = undefined
		}
		const requestId = ++this.requestId
		this.pendingController?.abort()
		const controller = new AbortController()
		this.pendingController = controller
		const editor = this.getEditor()
		const photo = editor.photo
		const candidateLeases: MaskTextureLease[] = []
		let candidateTransferred = false
		const committedObjectMaskIds = new Set(
			photo.adjustments.localAdjustments
				?.flatMap((adjustment) => adjustment.masks)
				.filter(isObjectMask)
				.map((mask) => mask.id) ?? [],
		)

		try {
			const acquiredMaskIds = new Set<string>()
			const transientEntries: Array<readonly [ArtefactId, unknown]> = []

			for (const mask of previewMasks) {
				if (!isObjectMask(mask)) {
					continue
				}
				if (committedObjectMaskIds.has(mask.id)) {
					continue
				}
				if (acquiredMaskIds.has(mask.id)) {
					continue
				}
				acquiredMaskIds.add(mask.id)
				const lease = await this.maskResolver.acquireMaskTexture(
					photo,
					mask,
					controller.signal,
				)
				candidateLeases.push(lease)
				transientEntries.push([{ type: "ai-mask", id: mask.id }, lease.texture])
			}

			if (!this.isCurrent(requestId, controller, editor, photo)) {
				this.releaseLeases(candidateLeases)
				return
			}

			const retiredLeases = this.activeTransientLeases
			const committedPreviewMaskIds = previewMasks
				.filter(isObjectMask)
				.map((mask) => mask.id)
				.filter((id) => committedObjectMaskIds.has(id))
			const handoffMaskIds = new Set([
				...committedPreviewMaskIds,
				...[...this.activeTransientMaskIds].filter((id) => committedObjectMaskIds.has(id)),
			])
			if (handoffMaskIds.size > 0) {
				// A preview mask may have just been committed. Wait for its normal renderer asset
				// before removing the transient resource that made the preview possible.
				await Promise.all(
					[...handoffMaskIds].map((id) =>
						editor.renderer.assets.resolve(photo, "ai-mask", id),
					),
				)
			}

			if (!this.isCurrent(requestId, controller, editor, photo)) {
				this.releaseLeases(candidateLeases)
				return
			}

			if (previewMasks.length > 0) {
				editor.renderer.assets.setTransientAssets(transientAssetScopeId, transientEntries)
				editor.setOverlays(null, previewMasks)
				this.activeTransientLeases = candidateLeases
				this.activeTransientMaskIds = acquiredMaskIds
				candidateTransferred = candidateLeases.length > 0
			} else {
				const maskIds = maskOverlayId ? new Set([maskOverlayId]) : null
				editor.setOverlays(maskIds, null)
				editor.renderer.assets.clearTransientAssets(transientAssetScopeId)
				this.activeTransientLeases = []
				this.activeTransientMaskIds = new Set()
			}

			this.releaseLeases(retiredLeases)
		} catch (error) {
			if (!candidateTransferred) {
				this.releaseLeases(candidateLeases)
			}
			if (!this.isCurrent(requestId, controller, editor, photo)) {
				return
			}

			if (!isAbortError(error)) {
				console.error("Failed to prepare transient mask overlay", error)
			}
			const retiredLeases = this.activeTransientLeases
			if (this.isCurrent(requestId, controller, editor, photo)) {
				editor.setOverlays(null, null)
				editor.renderer.assets.clearTransientAssets(transientAssetScopeId)
				this.activeTransientLeases = []
				this.activeTransientMaskIds = new Set()
				this.releaseLeases(retiredLeases)
			}
		} finally {
			if (this.requestId === requestId) {
				this.pendingController = undefined
			}
		}
	}

	reset() {
		if (this.updateTimeout !== undefined) {
			globalThis.clearTimeout(this.updateTimeout)
			this.updateTimeout = undefined
		}
		this.requestId += 1
		this.pendingController?.abort()
		this.pendingController = undefined
		const editor = this.getEditor()
		editor.setOverlays(null, null)
		editor.renderer.assets.clearTransientAssets(transientAssetScopeId)
		this.releaseLeases(this.activeTransientLeases)
		this.activeTransientLeases = []
		this.activeTransientMaskIds = new Set()
	}

	private isCurrent(
		requestId: number,
		controller: AbortController,
		editor: MaskOverlayEditor,
		photo: Photo,
	) {
		return (
			this.requestId === requestId &&
			!controller.signal.aborted &&
			this.getEditor() === editor &&
			editor.photo === photo
		)
	}

	private releaseLeases(leases: MaskTextureLease[]) {
		for (const lease of leases) {
			lease.release()
		}
	}
}
